import { useState } from "react";
import { cart } from "@/features/marketplace/stores/cart";
import { cartUi } from "@/features/marketplace/stores/cartUi";
import {
  LabProductCard,
  LabProductPreview,
  LAB_PRODUCT_IMAGE,
} from "./LabProductPreview";
import type { LabPanel, LabPurchaseType, LabInterval } from "@/types/labs";
import { useLabPreview } from "./labPreview";
import { LabHowItWorks } from "./LabHowItWorks";
import {
  useLabsExperiment,
  useLabView,
  useLabEvent,
} from "@/features/labs/labExperiment";
import { ArrowRight, FlaskConical } from "lucide-react";
import { Link } from "react-router-dom";
import { Card } from "@/design-system/primitives";
import { useLabCatalog } from "@/lib/api/labHooks";
import { LabError, LabSkeleton } from "./LabOrderContent";
import { labDisplayName } from "./labPresentation";

export function LabShop() {
  const { enabled: labsEnabled } = useLabsExperiment();
  const query = useLabCatalog(labsEnabled);
  const [selected, setSelected] = useState<LabPanel | null>(null);
  const preview = useLabPreview();
  const emit = useLabEvent();
  useLabView(
    "blood_test_shop_viewed",
    "shop_labs",
    labsEnabled && query.isSuccess,
  );
  const add = (
    panel: LabPanel,
    type: LabPurchaseType = "subscription",
    interval: LabInterval = 1,
  ) => {
    if (!panel.purchasable || preview.stage !== "actual") return;
    const added = cart.add({
      id: panel.product_id,
      slug: panel.panel_code,
      title: labDisplayName(panel.name),
      subtitle: panel.kit_description,
      price_cents:
        type === "subscription"
          ? panel.subscription_price_cents
          : panel.one_time_price_cents,
      lab_subscription_price_cents: panel.subscription_price_cents,
      lab_one_time_price_cents: panel.one_time_price_cents,
      image_url: LAB_PRODUCT_IMAGE,
      origin: "lab",
      lab_purchase_type: type,
      lab_interval_months: interval,
    });
    if (added)
      emit("blood_test_added_to_cart", {
        source: "shop_labs",
        product_id: panel.product_id,
        purchase_type: type,
        interval_months: type === "subscription" ? interval : undefined,
      });
    setSelected(null);
    cartUi.open();
  };
  if (!labsEnabled) return null;
  if (query.isLoading)
    return (
      <div className="p-20">
        <LabSkeleton label="Fetching lab tests" />
      </div>
    );
  if (query.isError)
    return (
      <div className="p-20">
        <LabError
          message="We couldn’t retrieve the lab tests. Try again."
          onRetry={() => void query.refetch()}
        />
      </div>
    );
  const panels = query.data?.enabled ? query.data.items : [];
  return (
    <section
      aria-label="Shop lab tests"
      className="px-15 py-25 flex flex-col gap-20"
    >
      <div className="flex flex-wrap items-center justify-between gap-15">
        <div>
          <h2 className="font-display text-24">Blood testing, from home.</h2>
          <p className="type-body-md text-ink-secondary mt-10">
            Choose your panel, collect your sample at home, and see your report
            in Labs.
          </p>
        </div>
        <Link
          to="/app/labs"
          className="text-14 underline min-h-44 inline-flex items-center gap-8"
        >
          Your tests and results
          <ArrowRight size={14} />
        </Link>
      </div>
      <LabHowItWorks source="shop_labs" />
      {!panels.length ? (
        <Card padding="lg">
          <FlaskConical
            size={24}
            aria-hidden
            className="text-ink-muted mb-15"
          />
          <h3 className="font-display text-20">
            Lab tests are currently unavailable.
          </h3>
          <p className="type-body-md text-ink-secondary mt-10">
            Your existing orders and reports are still available in Labs.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 gap-10 sm:gap-15">
          {panels.map((panel) => (
            <LabProductCard
              key={panel.product_id}
              panel={panel}
              previewing={preview.stage !== "actual"}
              onAdd={() => add(panel)}
              onOpen={() => {
                setSelected(panel);
                emit("blood_test_product_detail_viewed", {
                  source: "shop_labs",
                  product_id: panel.product_id,
                });
              }}
            />
          ))}
        </div>
      )}
      {selected && (
        <LabProductPreview
          key={selected.product_id}
          panel={selected}
          previewing={preview.stage !== "actual"}
          onClose={() => setSelected(null)}
          onAdd={(type, interval) => add(selected, type, interval)}
        />
      )}
    </section>
  );
}
